import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { z } from 'zod';
import { ArrowRight, Eye, EyeOff, Loader2, Lock, ShieldCheck, User } from 'lucide-react';
import { authService } from '@/services/auth';
import { twoFactorService } from '@/services/two-factor';
import { ApiError } from '@/lib/api-client';
import { useAuth } from '@/contexts/AuthContext';
import { resolveBrand } from '@/brand';
import type { LoginResponse } from '@just-frotas/shared';

// Schema do form. Versao client-side mais permissiva: backend valida com
// loginInputSchema do shared. Mantemos minimas validacoes de UX (campo
// obrigatorio + max length defensivo).
const loginFormSchema = z.object({
  email: z.string().trim().min(1, 'Email obrigatório').max(254),
  senha: z.string().min(1, 'Senha obrigatória').max(200),
});

type LoginFormValues = z.infer<typeof loginFormSchema>;

const TOTP_REGEX = /^\d{6}$/;

function mapLoginError(err: unknown): string {
  if (!(err instanceof ApiError)) return 'Não foi possível entrar agora.';
  if (err.status === 0) return 'Não foi possível conectar. Verifique sua rede.';
  if (err.code === 'ERR_CREDENCIAIS_INVALIDAS') return 'Email ou senha inválidos.';
  if (err.code === 'ERR_CONTA_BLOQUEADA') {
    return 'Conta bloqueada. Muitas tentativas falhadas. Tente novamente em 15 minutos.';
  }
  if (err.code === 'ERR_CONTA_INATIVA') return 'Sua conta está inativa. Fale com o administrador.';
  if (err.code === 'ERR_SEM_GRUPO_ACESSO') return 'Você não possui nenhum grupo de acesso cadastrado. Entre em contato com o seu gestor para regularizar seu acesso.';
  if (err.code === 'ERR_SEM_CONTEXTO_ACESSO') return 'O seu usuário não está associado a nenhum cliente / rede credenciada. Entre em contato com o seu gestor para regularizar seu acesso.';
  if (err.code === 'ERR_VALIDATION' && err.fields) return Object.values(err.fields).join(' ');
  if (err.status === 429 || err.code === 'ERR_RATE_LIMIT') {
    return 'Muitas tentativas. Aguarde e tente novamente.';
  }
  return err.detail || 'Email ou senha inválidos.';
}

function map2faError(err: unknown): string {
  if (!(err instanceof ApiError)) return 'Não foi possível verificar o código.';
  if (err.status === 0) return 'Sem conexão. Verifique sua rede.';
  if (err.code === 'ERR_2FA_INVALID_CODE') return 'Código inválido ou expirado. Tente novamente.';
  if (err.code === 'ERR_2FA_REQUIRED') return 'Sessão de verificação expirou. Faça login novamente.';
  return err.detail || 'Não foi possível verificar o código.';
}

export default function LoginPage() {
  // Chamado dentro do componente (nao no module scope) - evita depender da
  // ordem de avaliacao dos modulos no bundle de producao (ver mesmo
  // comentario em components/Sidebar.tsx).
  const brand = resolveBrand();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setSession } = useAuth();
  const [showPwd, setShowPwd] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [pendingChallenge, setPendingChallenge] = useState<string | null>(null);
  const [twoFaCode, setTwoFaCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [twoFaError, setTwoFaError] = useState<string | null>(null);
  const [twoFaSubmitting, setTwoFaSubmitting] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const twoFaInputRef = useRef<HTMLInputElement | null>(null);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: { email: '', senha: '' },
  });

  // Quando entra no step 2FA, foco vai pro input do codigo.
  useEffect(() => {
    if (pendingChallenge) {
      const t = setTimeout(() => twoFaInputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [pendingChallenge]);

  // Ja autenticado: nao mostra login. Volta pra rota original ou dashboard.
  if (user) {
    const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname ?? '/';
    return <Navigate to={from} replace />;
  }

  function applyLoginResponse(resp: LoginResponse) {
    setSession(resp.user, resp.accessToken);
    if (resp.user.firstAccess) {
      navigate('/reset-password?firstAccess=true', { replace: true });
      return;
    }
    const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname ?? '/';
    navigate(from, { replace: true });
  }

  const submitLogin = form.handleSubmit(async ({ email, senha }) => {
    setAuthError(null);
    try {
      const resp = await authService.login(email.trim(), senha);
      applyLoginResponse(resp);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'ERR_2FA_REQUIRED' && err.challengeToken) {
        setPendingChallenge(err.challengeToken);
        return;
      }
      setAuthError(mapLoginError(err));
    }
  });

  async function submitTwoFa() {
    if (!pendingChallenge) return;
    if (!TOTP_REGEX.test(twoFaCode.trim())) {
      setTwoFaError('Digite o código de 6 dígitos do seu app.');
      return;
    }
    setTwoFaError(null);
    setTwoFaSubmitting(true);
    try {
      const resp = await twoFactorService.verifyLogin(pendingChallenge, twoFaCode.trim(), {
        trustDevice,
      });
      applyLoginResponse(resp);
    } catch (err) {
      const message = map2faError(err);
      setTwoFaError(message);
      if (err instanceof ApiError && err.code === 'ERR_2FA_REQUIRED') {
        setTimeout(() => {
          setPendingChallenge(null);
          setTwoFaCode('');
        }, 1500);
      }
    } finally {
      setTwoFaSubmitting(false);
    }
  }

  function exitTwoFaStep() {
    setPendingChallenge(null);
    setTwoFaCode('');
    setTwoFaError(null);
    setTrustDevice(false);
  }

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-surface">
      {/* LEFT: form. Sem logo no canto - a marca aparece no hero direito.
          Mantemos o flex-col justify-between pra preservar o footer (c) JUST
          colado no rodape. */}
      <div className="flex flex-col justify-between p-10 lg:p-14 relative">
        {/* Spacer top: ocupa o lugar do antigo bloco do logo pra que o
            form continue centralizado verticalmente. */}
        <div aria-hidden="true" />

        <div className="max-w-md w-full mx-auto">
          <div className="mb-8">
            <p className="text-xs uppercase tracking-[0.15em] text-primary font-semibold mb-3">
              Plataforma de Gestão de Frota
            </p>
            <h1 className="text-3xl font-bold text-brand-navy-900 leading-tight tracking-tight">
              Bem-vindo de volta
            </h1>
            <p className="text-sm text-text-muted mt-2">
              Acesse sua conta para continuar gerenciando sua frota
            </p>
          </div>

          {pendingChallenge ? (
            <TwoFactorStep
              code={twoFaCode}
              onCodeChange={(v) => setTwoFaCode(v.replace(/\D/g, '').slice(0, 6))}
              onSubmit={submitTwoFa}
              submitting={twoFaSubmitting}
              error={twoFaError}
              trustDevice={trustDevice}
              onTrustDeviceChange={setTrustDevice}
              onCancel={exitTwoFaStep}
              inputRef={twoFaInputRef}
            />
          ) : (
            <form onSubmit={submitLogin} className="space-y-5" noValidate>
              <Field
                label="E-mail ou login"
                error={form.formState.errors.email?.message}
                icon={<User className="w-4 h-4" />}
              >
                <input
                  type="text"
                  autoComplete="username"
                  tabIndex={1}
                  placeholder="seu.usuario"
                  className="w-full pl-10 pr-3 py-3 text-sm bg-surface border border-border rounded-lg focus:outline-none focus:border-primary focus:shadow-focus-primary transition"
                  {...form.register('email')}
                />
              </Field>

              <Field
                label="Senha"
                error={form.formState.errors.senha?.message}
                icon={<Lock className="w-4 h-4" />}
              >
                <input
                  type={showPwd ? 'text' : 'password'}
                  autoComplete="current-password"
                  tabIndex={2}
                  placeholder="********"
                  className="w-full pl-10 pr-10 py-3 text-sm bg-surface border border-border rounded-lg focus:outline-none focus:border-primary focus:shadow-focus-primary transition"
                  {...form.register('senha')}
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowPwd((v) => !v)}
                  aria-label={showPwd ? 'Ocultar senha' : 'Mostrar senha'}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-text-subtle hover:text-text-strong"
                >
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </Field>

              {authError ? (
                <div
                  role="alert"
                  className="p-3 rounded-lg bg-danger-bg border border-danger/30 text-xs text-danger-text"
                >
                  {authError}
                </div>
              ) : null}

              <button
                type="submit"
                tabIndex={3}
                disabled={form.formState.isSubmitting}
                className="w-full py-3 px-4 text-on-primary font-semibold text-sm rounded-lg shadow-soft hover:shadow-card transition flex items-center justify-center gap-2 bg-primary hover:bg-primary-hover disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {form.formState.isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Autenticando...
                  </>
                ) : (
                  <>
                    Entrar na plataforma
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  tabIndex={4}
                  onClick={() => setForgotOpen(true)}
                  className="text-xs text-primary hover:text-primary-hover font-medium bg-transparent"
                >
                  Esqueci minha senha
                </button>
              </div>
            </form>
          )}
        </div>

        <div className="text-xs text-text-subtle">{brand.legalFooter}</div>
      </div>

      {/* RIGHT: hero - paleta do brand ativo. Espelha o deck original:
          radial superior esquerdo (accent 0.15) + radial inferior direito
          (accent 0.10) sobre gradient navy 900 -> 600. Usa var(--brand-*)
          em vez de hex cru pra herdar o brand ativo (VITE_BRAND). */}
      <div
        className="hidden lg:flex flex-col justify-center p-14 relative overflow-hidden"
        style={{
          background:
            'radial-gradient(ellipse at top left, rgb(var(--brand-goiaba-500-rgb) / 0.15), transparent 50%),' +
            'radial-gradient(ellipse at bottom right, rgb(var(--brand-goiaba-500-rgb) / 0.10), transparent 50%),' +
            'linear-gradient(135deg, var(--brand-navy-900) 0%, var(--brand-navy-900) 50%, var(--brand-navy-600) 100%)',
        }}
      >
        {/* Padrao de pontos sutis (igual deck) pra dar textura premium. */}
        <div
          className="absolute inset-0 pointer-events-none opacity-60"
          style={{
            backgroundImage:
              'radial-gradient(circle, rgba(255,255,255,0.06) 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
          aria-hidden="true"
        />

        <div className="relative z-10 max-w-md">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/5 backdrop-blur border border-white/10 rounded-full mb-6">
            <div className="w-2 h-2 rounded-full bg-brand-goiaba-500 animate-pulse" />
            <span className="text-[11px] font-semibold text-white/90 uppercase tracking-wider">
              Plataforma v2.0
            </span>
          </div>

          <h2 className="text-5xl font-bold text-white leading-tight tracking-tight">
            Sua frota,
            <br />
            <span
              className="text-transparent bg-clip-text"
              style={{
                // Gradient goiaba (igual highlight do deck oficial).
                backgroundImage:
                  'linear-gradient(135deg, var(--brand-goiaba-300), var(--brand-goiaba-500))',
              }}
            >
              sempre em movimento.
            </span>
          </h2>
          <p className="text-base text-white/70 mt-5 leading-relaxed">
            Controle total sobre abastecimento, motoristas e custos operacionais em uma única
            plataforma moderna e intuitiva.
          </p>

          <ul className="mt-10 grid grid-cols-2 gap-3">
            {['Terminal Web', 'Conciliação automática', 'Rede credenciada nacional', 'Relatórios em tempo real'].map(
              (label) => (
                <li key={label} className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-goiaba-500" />
                  <span className="text-xs text-white/70">{label}</span>
                </li>
              ),
            )}
          </ul>
        </div>

        {/* Logo do brand no canto inferior direito do hero. Reforca a marca
            sobre o navy sem competir com o headline. Opacidade 100% - o
            destaque do canto eh proposital (assinatura). */}
        <div className="absolute bottom-10 right-12 z-10 pointer-events-none select-none">
          <brand.Logo className="h-10 text-white" />
        </div>
      </div>

      {forgotOpen ? (
        <ForgotPasswordModal
          initialEmail={form.getValues('email')}
          onClose={() => setForgotOpen(false)}
        />
      ) : null}
    </div>
  );
}

interface FieldProps {
  label: string;
  error?: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}

function Field({ label, error, icon, children }: FieldProps) {
  return (
    <div>
      <label className="block text-xs font-semibold text-brand-navy-700 mb-2">{label}</label>
      <div className="relative">
        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-text-subtle">{icon}</span>
        {children}
      </div>
      {error ? <p className="mt-1.5 text-xs text-danger-text">{error}</p> : null}
    </div>
  );
}

interface TwoFactorStepProps {
  code: string;
  onCodeChange: (value: string) => void;
  onSubmit: () => void;
  submitting: boolean;
  error: string | null;
  trustDevice: boolean;
  onTrustDeviceChange: (value: boolean) => void;
  onCancel: () => void;
  inputRef: React.MutableRefObject<HTMLInputElement | null>;
}

function TwoFactorStep({
  code,
  onCodeChange,
  onSubmit,
  submitting,
  error,
  trustDevice,
  onTrustDeviceChange,
  onCancel,
  inputRef,
}: TwoFactorStepProps) {
  return (
    <div className="space-y-5">
      <div className="p-4 rounded-lg bg-brand-navy-50 border border-brand-navy-100">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-surface flex items-center justify-center flex-shrink-0">
            <ShieldCheck className="w-4 h-4 text-primary" />
          </div>
          <div className="text-[11px] text-brand-navy-700 leading-relaxed">
            <strong className="block text-brand-navy-900 text-xs mb-0.5">
              Verificação em duas etapas
            </strong>
            Abra seu app autenticador (Google Authenticator, Authy, 1Password) e digite o código de
            6 dígitos.
          </div>
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-brand-purple-700 mb-2">
          Código de verificação
        </label>
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={code}
          onChange={(e) => onCodeChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onSubmit();
            }
          }}
          placeholder="------"
          className="w-full px-4 py-3 text-center text-2xl font-mono tracking-[0.5em] bg-surface border border-border rounded-lg focus:outline-none focus:border-primary focus:shadow-focus-primary transition"
        />
      </div>

      <label className="flex items-start gap-2.5 p-3 border border-border rounded-lg cursor-pointer hover:border-border-strong transition">
        <input
          type="checkbox"
          checked={trustDevice}
          onChange={(e) => onTrustDeviceChange(e.target.checked)}
          className="mt-0.5 w-4 h-4 rounded border-border-strong accent-primary"
        />
        <div className="flex-1">
          <div className="text-xs font-semibold text-brand-purple-800">
            Confiar neste navegador
          </div>
          <div className="text-[11px] text-text-muted leading-relaxed mt-0.5">
            Não pediremos o código 2FA neste dispositivo até que você revogue em "Minha conta". Use
            apenas em computadores pessoais.
          </div>
        </div>
      </label>

      {error ? (
        <div
          role="alert"
          className="p-3 rounded-lg bg-danger-bg border border-danger/30 text-xs text-danger-text"
        >
          {error}
        </div>
      ) : null}

      <button
        type="button"
        onClick={onSubmit}
        disabled={submitting}
        className="w-full py-3 px-4 text-on-primary font-semibold text-sm rounded-lg shadow-soft hover:shadow-card transition flex items-center justify-center gap-2 bg-primary hover:bg-primary-hover disabled:opacity-70 disabled:cursor-not-allowed"
      >
        {submitting ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Verificando...
          </>
        ) : (
          <>
            Verificar e entrar
            <ArrowRight className="w-4 h-4" />
          </>
        )}
      </button>

      <button
        type="button"
        onClick={onCancel}
        className="w-full py-2 text-xs text-text-muted hover:text-text-strong bg-transparent"
      >
        Voltar para login
      </button>
    </div>
  );
}

interface ForgotPasswordModalProps {
  initialEmail: string;
  onClose: () => void;
}

function ForgotPasswordModal({ initialEmail, onClose }: ForgotPasswordModalProps) {
  const [email, setEmail] = useState(initialEmail.trim());
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<
    | { kind: 'ok'; text: string }
    | { kind: 'dev'; html: string }
    | { kind: 'error'; text: string }
    | null
  >(null);

  // ESC fecha modal
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function submit() {
    const trimmed = email.trim();
    if (!trimmed) {
      setFeedback({ kind: 'error', text: 'Informe um email válido.' });
      return;
    }
    setSubmitting(true);
    try {
      const resp = await authService.forgotPassword(trimmed);
      if (resp && typeof resp === 'object' && 'debugToken' in resp && resp.debugToken) {
        const url = `/reset-password?token=${encodeURIComponent(String(resp.debugToken))}`;
        setFeedback({
          kind: 'dev',
          // O backend devolve debugToken sempre que NODE_ENV !== 'production' e a
          // conta existe (FR-7, auth.controller.ts), independente de o email estar
          // configurado. Antes da story 1.1 o envio nao existia e o texto dizia
          // "email nao configurado"; hoje o email É enviado e o token vem junto,
          // entao o texto virou atalho de dev, nao aviso de falha.
          html: `<strong>Modo dev</strong>: link enviado por email. Atalho local:<br><a href="${url}" class="underline font-mono break-all">${url}</a>`,
        });
      } else {
        setFeedback({
          kind: 'ok',
          text: 'Se o email existir, enviaremos um link de redefinição em instantes.',
        });
      }
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 0) {
          setFeedback({ kind: 'error', text: 'Sem conexão com o servidor.' });
        } else if (err.code === 'ERR_VALIDATION' && err.fields) {
          setFeedback({ kind: 'error', text: Object.values(err.fields).join(' ') });
        } else {
          setFeedback({ kind: 'error', text: err.detail || 'Não foi possível processar agora.' });
        }
      } else {
        setFeedback({ kind: 'error', text: 'Erro inesperado.' });
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-modal bg-black/40 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-surface rounded-2xl max-w-md w-full p-6 shadow-modal">
        <h3 className="text-lg font-bold text-brand-navy-900 mb-1">Esqueci minha senha</h3>
        <p className="text-xs text-text-muted mb-4">
          Informe seu email de acesso. Se houver uma conta ativa, enviaremos um link de redefinição.
        </p>
        <label className="block text-xs font-semibold text-brand-navy-700 mb-1">
          Email de acesso
        </label>
        <input
          autoFocus
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full px-3 py-2.5 text-sm border border-border rounded-lg focus:outline-none focus:border-primary focus:shadow-focus-primary transition"
          placeholder="você@empresa.com"
        />
        {feedback ? (
          <div
            className={
              feedback.kind === 'ok'
                ? 'mt-3 p-3 rounded-lg text-xs bg-success-bg border border-success/30 text-success-text'
                : feedback.kind === 'dev'
                  ? 'mt-3 p-3 rounded-lg text-xs bg-warning-bg border border-warning/30 text-warning-text'
                  : 'mt-3 p-3 rounded-lg text-xs bg-danger-bg border border-danger/30 text-danger-text'
            }
          >
            {feedback.kind === 'dev' ? (
              <span dangerouslySetInnerHTML={{ __html: feedback.html }} />
            ) : (
              feedback.text
            )}
          </div>
        ) : null}

        <div className="flex gap-2 justify-end mt-5">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-text-muted hover:text-text-strong"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            className="px-4 py-2 text-sm text-on-primary font-semibold rounded-lg bg-primary hover:bg-primary-hover disabled:opacity-70"
          >
            {submitting ? 'Enviando...' : 'Enviar link'}
          </button>
        </div>
      </div>
    </div>
  );
}
